import { CircleInfo, FileText } from '@gravity-ui/icons'
import { Button, Modal, Spinner } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import type { DuplicateFileWarning } from '@/types/uploads'
import { formatDateTime } from '@/utils/dates'

interface DuplicateUploadModalProps {
  isOpen: boolean
  warningData: DuplicateFileWarning | null
  isUploading?: boolean
  onConfirm: () => void
  onCancel: () => void
}

export function DuplicateUploadModal({
  isOpen,
  warningData,
  isUploading = false,
  onConfirm,
  onCancel,
}: DuplicateUploadModalProps) {
  const { t } = useTranslation()

  if (!warningData) return null

  return (
    <Modal isOpen={isOpen} onOpenChange={open => !open && onCancel()}>
      <Modal.Backdrop variant="blur" className="bg-zinc-950/40" isDismissable>
        <Modal.Container placement="center">
          <Modal.Dialog>
            <Modal.CloseTrigger />
            <Modal.Header className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
                <CircleInfo width={22} height={22} />
              </div>
              <Modal.Heading className="font-bold text-lg">
                {t('uploads.duplicate_modal.title', 'Файл уже загружался')}
              </Modal.Heading>
            </Modal.Header>
            <Modal.Body className="mt-2 space-y-4">
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                {t(
                  'uploads.duplicate_modal.description',
                  'Обнаружен дубликат файла. Сервис зафиксировал ранее выгруженный файл с аналогичным именем:'
                )}
              </p>

              <div className="border border-amber-200 dark:border-amber-900/30 rounded-2xl p-4 bg-amber-50/40 dark:bg-amber-950/10 space-y-2">
                <div className="flex items-center gap-2.5">
                  <FileText className="size-4 text-amber-600 dark:text-amber-400 shrink-0" />
                  <span className="font-mono font-semibold text-sm truncate text-zinc-900 dark:text-zinc-100">
                    {warningData.Filename}
                  </span>
                </div>
                <div className="text-xs space-y-1 pt-1 border-t border-amber-200/60 dark:border-amber-900/20 text-zinc-600 dark:text-zinc-400">
                  <div>
                    <span className="font-medium text-zinc-700 dark:text-zinc-300">
                      {t('uploads.duplicate_modal.uploaded_by', 'Загрузил(а)')}:{' '}
                    </span>
                    <span>
                      {warningData.UploadedByName}
                      {warningData.UploadedBy ? ` (${warningData.UploadedBy})` : ''}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-zinc-700 dark:text-zinc-300">
                      {t('uploads.duplicate_modal.uploaded_at', 'Дата загрузки')}:{' '}
                    </span>
                    <span>{formatDateTime(warningData.UploadedAt)}</span>
                  </div>
                </div>
              </div>

              <p className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
                {t('uploads.duplicate_modal.confirm_question', 'Хотите загрузить повторно?')}
              </p>
            </Modal.Body>
            <Modal.Footer className="flex gap-3 justify-end mt-4">
              <Button variant="ghost" onClick={onCancel} isDisabled={isUploading}>
                {t('common.no', 'Нет')}
              </Button>
              <Button variant="primary" onClick={onConfirm} isDisabled={isUploading}>
                {isUploading ? (
                  <div className="flex items-center gap-2">
                    <Spinner size="sm" color="current" />
                    <span>{t('common.yes', 'Да, загрузить')}</span>
                  </div>
                ) : (
                  t('common.yes', 'Да, загрузить')
                )}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  )
}
